/**
 * CellEditModal — time-range editor with multi-day apply and constraint checking.
 *
 * Opens when user taps an editable grid cell in edit mode.
 * Supports:
 *  - Start/End TimeSpinner widgets with minTime enforcement on breakfast/lunch rows
 *  - "Apply times to" multi-day selector with All/Weekdays/Weekends presets
 *  - Constraint banner (earliest breakfast/lunch start based on operating channel open)
 *  - Overlap validation across breakfast/lunch/dinner rows
 *  - Violating-days warning (skipped on apply)
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CloseIcon, HStack, Pressable, VStack } from 'native-base';

import { AlertCircleIcon, InfoCircleIcon, Text } from '@pimm/base';
import {
  DailyHoursShape,
  EditValues,
  ActiveCell,
  EDITABLE_ROW_CONFIG,
  DAYS,
  DAYS_SHORT,
} from '../types';
import {
  formatTime,
  hhmmToMinutes,
  getConstraintForDays,
  getMinBreakfastStart,
  getOverlapError,
} from '../utils';
import { TimeSpinner } from './time-spinner';

type Props = {
  cell: ActiveCell;
  storeHours: DailyHoursShape[];
  editValues: EditValues;
  setEditValues: (v: EditValues) => void;
  originalValues: EditValues;
  onClose: () => void;
};

export const CellEditModal = ({
  cell,
  storeHours,
  editValues,
  setEditValues,
  originalValues,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const cfg = EDITABLE_ROW_CONFIG[cell.rowKey];
  const dayData  = editValues[cell.dayIdx];
  const origData = originalValues[cell.dayIdx];

  const [openVal,  setOpenVal]  = useState(dayData[cfg.openField]  || '');
  const [closeVal, setCloseVal] = useState(dayData[cfg.closeField] || '');
  const [selectedDays, setSelectedDays] = useState([cell.dayIdx]);
  const [activePreset, setActivePreset] = useState<'all' | 'weekdays' | 'weekends' | null>(null);

  const initOpenVal  = useRef<string | null>(null);
  const initCloseVal = useRef<string | null>(null);

  const handleOpenChange  = (v: string) => { if (initOpenVal.current  === null) initOpenVal.current  = openVal;  setOpenVal(v);  };
  const handleCloseChange = (v: string) => { if (initCloseVal.current === null) initCloseVal.current = closeVal; setCloseVal(v); };

  const hasLocalChange =
    (initOpenVal.current !== null  && openVal  !== initOpenVal.current) ||
    (initCloseVal.current !== null && closeVal !== initCloseVal.current);

  const openDirty  = origData && dayData[cfg.openField]  !== origData[cfg.openField];
  const closeDirty = origData && dayData[cfg.closeField] !== origData[cfg.closeField];

  const toggleDay = (idx: number) => {
    setActivePreset(null);
    setSelectedDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort((a, b) => a - b),
    );
  };

  const selectPreset = (p: 'all' | 'weekdays' | 'weekends') => {
    setActivePreset(p);
    if (p === 'all')           setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    else if (p === 'weekdays') setSelectedDays([0, 1, 2, 3, 4]);
    else                       setSelectedDays([5, 6]);
  };

  const isDayApplicable = (idx: number) => {
    if (storeHours[idx]?.isClosed) return false;
    return !cfg.enabledField || !!storeHours[idx][cfg.enabledField as keyof DailyHoursShape];
  };

  const isPresetEligible = (p: 'all' | 'weekdays' | 'weekends') => {
    const days = p === 'all' ? [0,1,2,3,4,5,6] : p === 'weekdays' ? [0,1,2,3,4] : [5,6];
    return days.some(isDayApplicable);
  };

  const applicableDays = selectedDays.filter(isDayApplicable);

  const isBreakfastRow = cell.rowKey === 'breakfast';
  const isLunchRow     = cell.rowKey === 'lunch';
  const lunchApplicableDays = isLunchRow ? applicableDays.filter((i) => !storeHours[i].servesBreakfast) : [];
  const constraintRow  = isBreakfastRow || (isLunchRow && lunchApplicableDays.length > 0);
  const constraintDays = isBreakfastRow ? applicableDays : lunchApplicableDays;

  const { minTime, constrainingDays } = constraintRow
    ? getConstraintForDays(constraintDays, storeHours, editValues)
    : { minTime: null, constrainingDays: [] };

  const overlapViolatingDays = applicableDays.filter((i) =>
    !!getOverlapError(t, cell.rowKey, openVal, closeVal, i, editValues, storeHours),
  );

  const violatingDays = constraintRow
    ? applicableDays.filter((i) => {
        if (isLunchRow && storeHours[i].servesBreakfast) return overlapViolatingDays.includes(i);
        const dayMin = getMinBreakfastStart(i, storeHours, editValues);
        if (dayMin && (hhmmToMinutes(openVal) ?? 0) < (hhmmToMinutes(dayMin) ?? 0)) return true;
        return overlapViolatingDays.includes(i);
      })
    : overlapViolatingDays;

  const validDays    = applicableDays.filter((i) => !violatingDays.includes(i));
  const overlapError = getOverlapError(t, cell.rowKey, openVal, closeVal, cell.dayIdx, editValues, storeHours);

  const constraintSkipDays = violatingDays.filter((i) => !overlapViolatingDays.includes(i));
  const overlapSkipDays    = violatingDays.filter((i) =>  overlapViolatingDays.includes(i));

  const canApply     = validDays.length > 0 && hasLocalChange;

  const handleApply = () => {
    if (!canApply) return;
    const updated = editValues.map((day, dayIdx) => {
      if (!validDays.includes(dayIdx)) return day;
      return { ...day, [cfg.openField]: openVal, [cfg.closeField]: closeVal };
    });
    setEditValues(updated);
    onClose();
  };

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={10}>
      <Pressable
        position="absolute" top={0} left={0} right={0} bottom={0}
        bg="rgba(0,0,0,0.5)" onPress={onClose}
      />
      <Box
        position="absolute" top={0} left={0} right={0} bottom={0}
        alignItems="center" justifyContent="center"
        // @ts-ignore — pointerEvents is a valid RNW prop
        pointerEvents="box-none"
      >
        <Box bg="white" rounded="xl" w="90%" maxW="560px" shadow={9}>
          {/* Modal header */}
          <HStack alignItems="center" justifyContent="space-between" px={5} pt={4} pb={3}
            borderBottomWidth={1} borderColor="gray.200">
            <VStack space={0.5} flex={1}>
              <Text size="md" fontWeight={600} color="gray.900">{t(cfg.label)}</Text>
              <Text size="sm" color="gray.500">{DAYS[cell.dayIdx]}</Text>
            </VStack>
            <Pressable onPress={onClose}>
              <CloseIcon size={4} color="gray.400" />
            </Pressable>
          </HStack>

          <Box px={5} py={4}>
            {/* Time spinners */}
            <HStack space={0} alignItems="flex-start" mb={3}>
              <TimeSpinner
                label={t(cfg.openLabel)}
                value={openVal}
                onChange={handleOpenChange}
                minTime={constraintRow ? minTime : null}
              />
              <Box w="1px" bg="gray.200" mx={4} alignSelf="stretch" />
              <TimeSpinner
                label={t(cfg.closeLabel)}
                value={closeVal}
                onChange={handleCloseChange}
              />
            </HStack>

            {/* Constraint banner */}
            {constraintRow && minTime && (
              <HStack alignItems="center" space={1.5} mb={2} p={2}
                bg="warning.50" borderWidth={1} borderColor="warning.200" rounded="md">
                <AlertCircleIcon size={3} color="warning.700" />
                <Text size="xs" color="warning.800" flex={1}>
                  {t('store_settings:earliest_start_label')}{' '}
                  <Text size="xs" fontWeight={700} color="warning.800">{formatTime(minTime)}</Text>
                  {constrainingDays.length > 0 && (
                    <Text size="xs" color="gray.400">
                      {' '}{t('store_settings:earliest_start_limited_by', { days: constrainingDays.map((i) => DAYS_SHORT[i]).join(', ') })}
                    </Text>
                  )}
                  {isLunchRow && lunchApplicableDays.length > 0 && (
                    <Text size="xs" color="gray.400"> {t('store_settings:earliest_start_no_breakfast_suffix')}</Text>
                  )}
                </Text>
              </HStack>
            )}

            {/* Overlap error banner */}
            {overlapError && (
              <HStack alignItems="center" space={1.5} mb={2} p={2}
                bg="error.50" borderWidth={1} borderColor="error.200" rounded="md">
                <AlertCircleIcon size={3} color="error.700" />
                <Text size="xs" color="error.700">{overlapError}</Text>
              </HStack>
            )}

            {/* Original → staged diff (shows when user opened a previously-changed cell) */}
            {(openDirty || closeDirty) && (
              <HStack space={2} mb={3}>
                {openDirty ? (
                  <HStack alignItems="center" space={1} flex={1}>
                    <Text size="xs" color="gray.400" textDecorationLine="line-through">
                      {formatTime(origData[cfg.openField])}
                    </Text>
                    <Text size="xs" color="gray.400">{'→'}</Text>
                    <Text size="xs" fontWeight={700} color="warning.700">
                      {formatTime(dayData[cfg.openField])}
                    </Text>
                  </HStack>
                ) : <Box flex={1} />}
                {closeDirty ? (
                  <HStack alignItems="center" space={1} flex={1}>
                    <Text size="xs" color="gray.400" textDecorationLine="line-through">
                      {formatTime(origData[cfg.closeField])}
                    </Text>
                    <Text size="xs" color="gray.400">{'→'}</Text>
                    <Text size="xs" fontWeight={700} color="warning.700">
                      {formatTime(dayData[cfg.closeField])}
                    </Text>
                  </HStack>
                ) : <Box flex={1} />}
              </HStack>
            )}

            {/* Apply-to section */}
            <Text size="xs" fontWeight={500} color="gray.400" mb={2}>
              {t('store_settings:apply_times_to')}
            </Text>

            {/* Preset shortcuts */}
            <HStack space={2} mb={3} justifyContent="center">
              {(['all', 'weekdays', 'weekends'] as const).map((key) => {
                const eligible = isPresetEligible(key);
                const active   = activePreset === key;
                const presetLabel = key === 'all'
                  ? t('store_settings:all_days')
                  : key === 'weekdays'
                  ? t('store_settings:weekdays')
                  : t('store_settings:weekends');
                return (
                  <Pressable key={key} onPress={eligible ? () => selectPreset(key) : undefined}
                    disabled={!eligible} opacity={eligible ? 1 : 0.35}>
                    <Box px={3} py={1} rounded="full" borderWidth={1}
                      borderColor={active ? 'gray.800' : 'gray.300'}
                      bg={active ? 'gray.800' : 'white'}
                      alignItems="center">
                      <Text size="xs" fontWeight={600} color={active ? 'white' : 'gray.600'}>
                        {presetLabel}
                      </Text>
                    </Box>
                  </Pressable>
                );
              })}
            </HStack>

            {/* Individual day tiles */}
            <HStack space={1} mb={3}>
              {DAYS.map((_, idx) => {
                const applicable = isDayApplicable(idx);
                const selected   = selectedDays.includes(idx);
                return (
                  <Pressable key={idx} flex={1} onPress={applicable ? () => toggleDay(idx) : undefined} disabled={!applicable}
                    h={8}>
                    <Box
                      alignItems="center" justifyContent="center" rounded="md"
                      borderWidth={1}
                      borderColor={!applicable ? 'gray.200' : selected ? 'gray.800' : 'gray.300'}
                      bg={!applicable ? 'gray.50' : selected ? 'gray.800' : 'white'}
                      shadow={applicable && selected ? 1 : 0}
                      h="full" w="full"
                    >
                      <Text size="xs" fontWeight={600}
                        color={!applicable ? 'gray.300' : selected ? 'white' : 'gray.500'}>
                        {applicable ? DAYS_SHORT[idx] : '—'}
                      </Text>
                    </Box>
                  </Pressable>
                );
              })}
            </HStack>

            {/* Ineligible days note */}
            {(() => {
              const ineligible = DAYS.filter((_, idx) => !isDayApplicable(idx));
              if (!ineligible.length || !cfg.ineligibleReason) return null;
              return (
                <HStack alignItems="flex-start" space={1.5} mb={2} p={2} bg="gray.50" rounded="md">
                  <InfoCircleIcon size={3} color="gray.400" />
                  <Text size="xs" color="gray.500" flex={1}>
                    <Text size="xs" fontWeight={600} color="gray.600">{ineligible.join(', ')}</Text>
                    {' '}&mdash; {t(cfg.ineligibleReason!)}
                  </Text>
                </HStack>
              );
            })()}

            {/* Violating days warning — constraint (start too early) */}
            {constraintSkipDays.length > 0 && (
              <HStack alignItems="flex-start" space={1.5} p={2}
                bg="error.50" borderWidth={1} borderColor="error.200" rounded="md">
                <AlertCircleIcon size={3} color="error.700" />
                <Text size="xs" color="error.700" flex={1}>
                  <Text size="xs" fontWeight={700} color="error.700">
                    {constraintSkipDays.map((i) => DAYS_SHORT[i]).join(', ')}
                  </Text>
                  {' '}{t(isLunchRow ? 'store_settings:skip_constraint_lunch' : 'store_settings:skip_constraint_breakfast')}
                </Text>
              </HStack>
            )}

            {/* Violating days warning — overlap (end time conflicts with next/prev period) */}
            {overlapSkipDays.length > 0 && (
              <HStack alignItems="flex-start" space={1.5} p={2}
                bg="error.50" borderWidth={1} borderColor="error.200" rounded="md">
                <AlertCircleIcon size={3} color="error.700" />
                <Text size="xs" color="error.700" flex={1}>
                  <Text size="xs" fontWeight={700} color="error.700">
                    {overlapSkipDays.map((i) => DAYS_SHORT[i]).join(', ')}
                  </Text>
                  {' '}{t(
                    isLunchRow   ? 'store_settings:skip_overlap_lunch'
                    : isBreakfastRow ? 'store_settings:skip_overlap_breakfast'
                    : 'store_settings:skip_overlap_dinner'
                  )}
                </Text>
              </HStack>
            )}
          </Box>

          {/* Footer */}
          <HStack space={3} justifyContent="flex-end" px={5} py={4}
            borderTopWidth={1} borderColor="gray.200">
            <Pressable onPress={onClose}>
              <Box px={4} py={2} rounded="lg" borderWidth={1} borderColor="gray.300" bg="white" shadow={1}>
                <Text size="sm" fontWeight={600} color="gray.700">{t('store_settings:cancel')}</Text>
              </Box>
            </Pressable>
            <Pressable onPress={handleApply} disabled={!canApply} opacity={canApply ? 1 : 0.4}>
              <Box px={4} py={2} rounded="lg" bg="primary.500" shadow={1}>
                <Text size="sm" fontWeight={600} color="black">
                  {violatingDays.length > 0 && validDays.length > 0
                    ? t('store_settings:apply_to_days', { count: validDays.length })
                    : t('store_settings:apply')}
                </Text>
              </Box>
            </Pressable>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};
